import { lazy } from "react";
import { Redirect, Switch } from "react-router";

import { RoutePathBuilder } from "@/common/builders/routePath";
import AuthenticatedGuard from "@/common/components/Auth/AuthenticatedGuard";
import AuthorizedGuard from "@/common/components/Auth/AuthorizedGuard";
import AuthorizedRoute from "@/common/components/Auth/AuthorizedRoute";
import { Loadable } from "@/common/components/Loadable";
import AppRoute from "@/common/components/Routing/AppRoute";
import { ROUTE_PATH } from "@/common/constants/routing";
import { AppPermission } from "@/core/api/generated/v1";

import GlobalAlertsForAllAppAreas from "../BaseApp/GlobalAlerts/GlobalAlertsForAllAppAreas";
import FleetAppLayout from "../Layouts/FleetAppLayout";
import { AffiliationRoutes } from "./FleetApp/AffiliationRoutes";
import { CommunicationRoutes } from "./FleetApp/CommunicationRoutes";
import { EntityRoutes } from "./FleetApp/EntityRoutes";
import { FlowRoutes } from "./FleetApp/FlowRoutes";
import { HistoryRoutes } from "./FleetApp/HistoryRoutes";
import { ManagementRoutes } from "./FleetApp/ManagementRoutes";
import { OperationRoutes } from "./FleetApp/OperationRoutes";
import { OtherRoutes } from "./FleetApp/OtherRoutes";
import { ProductRoutes } from "./FleetApp/ProductRoutes";
import { ProfileRoutes } from "./FleetApp/ProfileRoutes";
import { ReferenceDataRoutes } from "./FleetApp/ReferenceDataRoutes";
import { RepairRoutes } from "./FleetApp/RepairRoutes";
import { TenantToTenantRoutes } from "./FleetApp/TenantToTenantRoutes";
import { VehicleRoutes } from "./FleetApp/VehicleRoutes";

const FleetCustomer_Dashboard = Loadable(
  lazy(() => import("../FleetCustomerApp/Dashboard/Dashboard")),
);

const ContractCreateUpdatePage = Loadable(
  lazy(() => import("../MainAppView/Contracts/ContractCreateUpdatePage")),
);
const ContractViewPage = Loadable(lazy(() => import("../MainAppView/Contracts/ContractViewPage")));
const ContractsPage = Loadable(lazy(() => import("../MainAppView/Contracts/ContractsPage")));
const CustomerCreateUpdatePage = Loadable(
  lazy(() => import("../MainAppView/Customers/CustomerCreateUpdatePage")),
);
const CustomerViewPage = Loadable(lazy(() => import("../MainAppView/Customers/CustomerViewPage")));
const CustomersPage = Loadable(lazy(() => import("../MainAppView/Customers/CustomersPage")));

const DashboardV1 = Loadable(
  lazy(() =>
    import("../MainAppView/Dashboard/v1/DashboardV1").then((x) => ({ default: x.DashboardV1 })),
  ),
);
const Dashboard = Loadable(
  lazy(() =>
    import("../MainAppView/Dashboard/v2/Dashboard").then((x) => ({ default: x.Dashboard })),
  ),
);

const OnboardingViewPage = Loadable(
  lazy(() => import("../MainAppView/Onboarding/OnboardingViewPage")),
);

const FlowsPage = Loadable(lazy(() => import("../MainAppView/Flows/FlowsPage")));
const InboxPage = Loadable(lazy(() => import("../MainAppView/Communication/Inbox/InboxPage")));
const CommunicationPage = Loadable(
  lazy(() => import("../MainAppView/Communication/CommunicationPage")),
);

const UserViewPage = Loadable(lazy(() => import("../MainAppView/Management/Users/UserViewPage")));

const OperationsPage = Loadable(lazy(() => import("../MainAppView/Operations/OperationsPage")));
const OtherPage = Loadable(lazy(() => import("../MainAppView/Other/OtherPage")));

const ProductsIndexPage = Loadable(lazy(() => import("../MainAppView/Products/ProductsIndexPage")));
const ReferenceDataIndexPage = Loadable(
  lazy(() => import("../MainAppView/ReferenceData/ReferenceDataIndexPage")),
);
const RepairPage = Loadable(lazy(() => import("../MainAppView/Repair/RepairPage")));

const RootRouteEntry = Loadable(lazy(() => import("../MainAppView/RootRouteEntry")));

const TenantViewPage = Loadable(lazy(() => import("../MainAppView/Tenant/TenantViewPage")));

const TenantToTenantIndexPage = Loadable(
  lazy(() => import("../MainAppView/TenantToTenant/TenantToTenantIndexPage")),
);

const NotFoundErrorPage = Loadable(lazy(() => import("../BaseApp/Error/NotFoundErrorPage")));
const ManagementPage = Loadable(lazy(() => import("../MainAppView/Management/ManagementPage")));
const VehiclesPage = Loadable(lazy(() => import("../MainAppView/Vehicles/VehiclesPage")));

export default function FleetAppContent() {
  // console.log("FleetAppContent", moment(performance.now()).format("HH:mm:ss.SSS"));

  return (
    <AuthenticatedGuard>
      {/* FleetCustomer has access to some pages of FleetApp */}
      <AuthorizedGuard
        permissionsAny={[AppPermission.FleetAppAccess, AppPermission.FleetCustomerAppAccess]}
      >
        <>
          {/* Global alerts */}
          <GlobalAlertsForAllAppAreas />

          <FleetAppLayout>
            {/* NB: don't use React.Fragment in Switch as it breaks routes. */}
            <Switch>
              {/* Profile */}
              <AuthorizedRoute
                permissionsAny={[
                  AppPermission.FleetAppAccess,
                  AppPermission.FleetCustomerAppAccess,
                ]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.FLEET_PROFILE)}
                render={() => <Redirect to={ROUTE_PATH.FLEET_PROFILE_VIEW} />}
              />
              <AuthorizedRoute
                permissionsAny={[
                  AppPermission.FleetAppAccess,
                  AppPermission.FleetCustomerAppAccess,
                ]}
                exact
                path={`${RoutePathBuilder.toDefinition(ROUTE_PATH.FLEET_PROFILE)}/*`}
                render={() => <ProfileRoutes />}
              />

              {/* <AppRoute
              exact
              path={RoutePathBuilder.toDefinition(ROUTE_PATH.FLEET_PROFILE)}
              render={() => <Profile />}
            />
            <AppRoute
              exact
              path={RoutePathBuilder.toDefinition(ROUTE_PATH.FLEET_PROFILE_PERSONAL_INFO_EDIT)}
              render={() => <ProfilePersonalInfoEdit />}
            />
            <AppRoute
              exact
              path={RoutePathBuilder.toDefinition(ROUTE_PATH.FLEET_PROFILE_SETTINGS_EDIT)}
              render={() => <ProfileSettingsEdit />}
            /> */}

              {/* Dashboard */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.DASHBOARD_TASKS)}
                render={() => <DashboardV1 />}
              />
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]} // skip DashboardRead as dashboard must be always available even if empty
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.DASHBOARD)}
                render={() => <Dashboard />}
              />
              <AuthorizedRoute
                permissions={[AppPermission.FleetCustomerAppAccess]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.FLEET_CUSTOMER_DASHBOARD)}
                render={() => <FleetCustomer_Dashboard />}
              />

              {/* Onboarding */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.ONBOARDING_VIEW())}
                render={() => <OnboardingViewPage />}
              />

              {/* Management */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT)}
                render={() => <ManagementPage />}
              />
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={`${RoutePathBuilder.toDefinition(ROUTE_PATH.MANAGEMENT)}/*`}
                render={() => <ManagementRoutes />}
              />

              {/* Tenant */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess, AppPermission.TenantRead]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.TENANT_VIEW())}
                render={() => <TenantViewPage />}
              />

              {/* Affiliation */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={`${RoutePathBuilder.toDefinition(ROUTE_PATH.AFFILIATION)}/*`}
                render={() => <AffiliationRoutes />}
              />

              {/* User */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess, AppPermission.TenantUserRead]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.USER_VIEW())}
                render={() => <UserViewPage />}
              />

              {/* Entity */}
              <AppRoute
                exact
                path={`${RoutePathBuilder.toDefinition(ROUTE_PATH.ENTITIES())}/*`}
                render={() => <EntityRoutes />}
              />

              {/* Vehicle */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess, AppPermission.VehicleRead]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.VEHICLES)}
                render={() => <VehiclesPage />}
              />
              <AppRoute
                exact
                path={`${RoutePathBuilder.toDefinition(ROUTE_PATH.VEHICLES)}/*`}
                render={() => <VehicleRoutes />}
              />

              {/* Customer */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess, AppPermission.CustomerRead]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.CUSTOMERS)}
                render={() => <CustomersPage />}
              />

              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess, AppPermission.CustomerRead]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.CUSTOMER_VIEW())}
                render={() => <CustomerViewPage />}
              />

              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess, AppPermission.CustomerManage]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.CUSTOMER_CREATE)}
                render={() => <CustomerCreateUpdatePage />}
              />

              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess, AppPermission.CustomerManage]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.CUSTOMER_EDIT())}
                render={() => <CustomerCreateUpdatePage />}
              />

              {/* Contract */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess, AppPermission.ContractRead]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.CONTRACTS)}
                render={() => <ContractsPage />}
              />
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess, AppPermission.ContractManage]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.CONTRACT_CREATE())}
                render={() => <ContractCreateUpdatePage />}
              />
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess, AppPermission.ContractRead]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.CONTRACT_VIEW())}
                render={() => <ContractViewPage />}
              />
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess, AppPermission.ContractManage]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.CONTRACT_EDIT())}
                render={() => <ContractCreateUpdatePage />}
              />

              {/* Operations */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.OPERATIONS)}
                render={() => <OperationsPage />}
              />
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={`${RoutePathBuilder.toDefinition(ROUTE_PATH.OPERATIONS)}/*`}
                render={() => <OperationRoutes />}
              />

              {/* Document */}
              {/* NB: temporary disabled */}
              {/* <Box>
              <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
                <AuthorizedElement permissions={[AppPermission.DocumentRead]}>
                  <AppRoute
                    exact
                    path={RoutePathBuilder.toDefinition(ROUTE_PATH.DOCUMENTS())}
                    render={() => <DocumentsPage />}
                  />
                </AuthorizedElement>
                <AuthorizedElement permissions={[AppPermission.DocumentRead]}>
                  <AppRoute
                    exact
                    path={ROUTE_PATH.DOCUMENTS_DAMAGE_DETECTIONS(true)}
                    render={() => <DocumentsPage />}
                  />
                </AuthorizedElement>
                <AuthorizedElement permissions={[AppPermission.DocumentRead]}>
                  <AppRoute
                    exact
                    path={ROUTE_PATH.DOCUMENTS_DAMAGE_COST_EVALUATIONS(true)}
                    render={() => <DocumentsPage />}
                  />
                </AuthorizedElement>
                <AuthorizedElement permissions={[AppPermission.DocumentRead]}>
                  <AppRoute
                    exact
                    path={ROUTE_PATH.DOCUMENTS_ACCESSORY_CHECKS(true)}
                    render={() => <DocumentsPage />}
                  />
                </AuthorizedElement>
              </AuthorizedElement>
              <AuthorizedElement
                permissionsAny={[AppPermission.FleetAppAccess, AppPermission.FleetCustomerAppAccess]}
              >
                <AuthorizedElement permissions={[AppPermission.DocumentRead]}>
                  <AppRoute
                    exact
                    path={RoutePathBuilder.toDefinition(ROUTE_PATH.DOCUMENT_VIEW())}
                    render={() => <DocumentViewContainer />}
                  />
                </AuthorizedElement>
              </AuthorizedElement>
            </Box> */}

              {/* Flows */}
              <AuthorizedRoute
                permissionsAny={[
                  AppPermission.FleetAppAccess,
                  AppPermission.FleetCustomerAppAccess,
                ]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.FLOWS)}
                render={() => <FlowsPage />}
              />
              <AuthorizedRoute
                permissionsAny={[
                  AppPermission.FleetAppAccess,
                  AppPermission.FleetCustomerAppAccess,
                ]}
                exact
                path={`${RoutePathBuilder.toDefinition(ROUTE_PATH.FLOWS)}/*`}
                render={() => <FlowRoutes />}
              />

              {/* Inbox */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.INBOX())}
                render={() => <InboxPage />}
              />

              {/* Repair */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess, AppPermission.RepairCatalogRead]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.REPAIR)}
                render={() => <RepairPage />}
              />
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess, AppPermission.RepairCatalogRead]}
                exact
                path={`${RoutePathBuilder.toDefinition(ROUTE_PATH.REPAIR)}/*`}
                render={() => <RepairRoutes />}
              />

              {/* ReferenceData */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.REFERENCE_DATA_INDEX)}
                render={() => <ReferenceDataIndexPage />}
              />
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={`${RoutePathBuilder.toDefinition(ROUTE_PATH.REFERENCE_DATA)}/*`}
                render={() => <ReferenceDataRoutes />}
              />

              {/* Products */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.PRODUCTS_INDEX)}
                render={() => <ProductsIndexPage />}
              />
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={`${RoutePathBuilder.toDefinition(ROUTE_PATH.PRODUCTS)}/*`}
                render={() => <ProductRoutes />}
              />

              {/* Tenant-to-Tenant */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.TENANT_TO_TENANT_INDEX)}
                render={() => <TenantToTenantIndexPage />}
              />
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={`${RoutePathBuilder.toDefinition(ROUTE_PATH.TENANT_TO_TENANT)}/*`}
                render={() => <TenantToTenantRoutes />}
              />

              {/* Communication */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.COMMUNICATION_INDEX())}
                render={() => <CommunicationPage />}
              />
              <AuthorizedRoute
                permissions={[]}
                exact
                path={`${RoutePathBuilder.toDefinition(ROUTE_PATH.COMMUNICATION())}/*`}
                render={() => <CommunicationRoutes />}
              />

              {/* History */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={`${RoutePathBuilder.toDefinition(ROUTE_PATH.HISTORY)}/*`}
                render={() => <HistoryRoutes />}
              />

              {/* Other */}
              <AuthorizedRoute
                permissions={[AppPermission.FleetAppAccess]}
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.OTHER)}
                render={() => <OtherPage />}
              />
              <AuthorizedRoute
                permissions={[]}
                exact
                path={`${RoutePathBuilder.toDefinition(ROUTE_PATH.OTHER)}/*`}
                render={() => <OtherRoutes />}
              />

              <AppRoute
                exact
                path={ROUTE_PATH.APP_AREA_FLEET_APP}
                render={() => <RootRouteEntry />}
              />

              <AppRoute render={() => <NotFoundErrorPage />} />
            </Switch>
          </FleetAppLayout>
        </>
      </AuthorizedGuard>
    </AuthenticatedGuard>
  );
}
